import { createGlobalState, useAsyncState } from '@vueuse/core'
import {
  getShareDetail,
  shareCommissionProportion,
  shareInviteImage,
  shareInviteUrl
} from '@/api/share'
import type { CommissionProportion, InviteImageRes, InviteLinkUrl } from '@/types/share'
import { useConstConfigV2 } from '@/composables/useConstConfigV2'
import { getWebpUrl } from '@/utils/webpAssets'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

export interface ShareItem {
  id: number
  name: string
  src: string
  link: string
}

interface GetShareIconsOptions {
  hideCustom?: boolean
}

interface FetchShareOptions {
  force?: boolean
}

export const useShareDetail = createGlobalState(() => {
  const { getShareConfig } = useConstConfigV2()

  // 分享详情：域名、推广说明、联系方式等基础信息。
  const {
    isLoading: detailLoading,
    state: detailData,
    execute: loadShareDetail
  } = useAsyncState(
    async () => {
      const res = await getShareDetail()
      return res.data || null
    },
    null,
    { immediate: false, resetOnExecute: false }
  )

  // 返佣比例：分享页和推广教程复用的展示数据。
  // 分享配置：控制分享页面 tab 和个性化入口显隐。
  const {
    state: scrollData,
    isLoading: scrollLoading,
    execute: loadCommissionProportion
  } = useAsyncState(
    async () => {
      const res = await shareCommissionProportion()
      return res.data || []
    },
    [] as CommissionProportion[],
    { immediate: false, resetOnExecute: false }
  )

  const {
    state: shareConfig,
    isLoading: shareConfigLoading,
    isReady: shareConfigReady,
    execute: loadShareConfig
  } = useAsyncState(
    async () => {
      return await getShareConfig()
    },
    {
      sms: false,
      poster: false,
      link: false,
      step: false,
      platform: false,
      reward: false
    },
    { immediate: false, resetOnExecute: false }
  )

  // 个性分享-分享链接
  const {
    state: inviteUrlData,
    isLoading: inviteUrlLoading,
    isReady: inviteUrlLoaded,
    execute: loadInviteUrls
  } = useAsyncState(
    async () => {
      const res = await shareInviteUrl({
        model: {
          type: 1
        }
      })
      return res.data?.records ?? []
    },
    [] as InviteLinkUrl[],
    { immediate: false, resetOnExecute: false }
  )

  // 个性分享-分享海报
  const {
    state: inviteImageData,
    isLoading: inviteImageLoading,
    isReady: inviteImageLoaded,
    execute: loadInviteImages
  } = useAsyncState(
    async () => {
      const res = await shareInviteImage({
        model: {
          type: 1
        }
      })
      return res.data?.records ?? []
    },
    [] as InviteImageRes[],
    { immediate: false, resetOnExecute: false }
  )

  const fetchDetail = async (options: FetchShareOptions = {}) => {
    if (detailLoading.value) return
    // 有缓存默认直接使用响应式数据；force 用于手动刷新后台数据。
    if (!options.force && detailData.value) return

    await loadShareDetail()
  }

  const fetchScrollData = async (options: FetchShareOptions = {}) => {
    if (scrollLoading.value) return
    // 返佣比例为展示数据，有缓存时不阻塞页面重新渲染。
    if (!options.force && scrollData.value.length > 0) return

    await loadCommissionProportion()
  }

  const fetchShareConfig = async (options: FetchShareOptions = {}) => {
    if (shareConfigLoading.value) return
    // 配置可能全为 false，用 ready 判断是否已有有效缓存。
    if (!options.force && shareConfigReady.value) return

    await loadShareConfig()
  }

  const fetchInviteUrls = async (options: FetchShareOptions = {}) => {
    if (inviteUrlLoading.value) return
    // 个性分享列表由组件直接读取 inviteUrlData。
    if (!options.force && inviteUrlData.value.length > 0) return

    await loadInviteUrls()
  }

  const fetchInviteImages = async (options: FetchShareOptions = {}) => {
    if (inviteImageLoading.value) return
    // 海报组件负责把响应式图片数据加工成可下载海报。
    if (!options.force && inviteImageData.value.length > 0) return

    await loadInviteImages()
  }

  // 分享链接
  const shareLink = computed(() => {
    const url = detailData.value?.domain || ''
    if (url && url.startsWith('https://')) {
      return url
    } else if (url) {
      return 'https://' + url
    } else {
      return ''
    }
  })

  // 分享文案
  const { t } = useI18n()
  const shareText = computed(() => t(''))

  // 获取分享图标列表
  const getShareIcons = (
    overrideShareLink?: string,
    options: GetShareIconsOptions = {}
  ): ShareItem[] => {
    const finalShareLink = overrideShareLink || shareLink.value
    const icons: ShareItem[] = [
      {
        id: 1,
        src: getWebpUrl('imgs/share/icon_facebook.webp'),
        name: 'Facebook',
        link: `https://www.facebook.com/share_channel/?link=${finalShareLink}&source_surface=external_reshare&display&hashtag`
      },
      {
        id: 3,
        src: getWebpUrl('imgs/share/icon_telegram.webp'),
        name: 'Telegram',
        link: `https://t.me/share/url?text=${shareText.value}&url=${finalShareLink}`
      },
      {
        id: 4,
        src: getWebpUrl('imgs/share/icon_x.webp'),
        name: 'X',
        link: `https://twitter.com/intent/tweet?text=${shareText.value}&url=${finalShareLink}`
      },
      {
        id: 2,
        src: getWebpUrl('imgs/share/icon_whatsapp.webp'),
        name: 'WhatsApp',
        link: `https://wa.me/?text=${shareText.value}\n${finalShareLink}`
      },
      {
        id: 5,
        src: getWebpUrl('imgs/share/icon_gmail.webp'),
        name: 'Email',
        link: `mailto:?&body=${shareText.value}\n${finalShareLink}`
      }
    ]

    // 个性化分享入口由后台链接/海报分享配置控制，调用方可按场景强制隐藏。
    if (!options.hideCustom && (shareConfig.value.link || shareConfig.value.poster)) {
      icons.push({
        id: 6,
        src: getWebpUrl('imgs/share/icon_personal.webp'),
        name: t('个性化'),
        link: '/share/custom'
      })
    }

    return icons
  }

  return {
    detailData,
    scrollData,
    shareLink,
    detailLoading,
    scrollLoading,
    fetchDetail,
    fetchScrollData,
    fetchShareConfig,
    getShareIcons,
    shareConfig,
    shareConfigLoading,
    shareConfigReady,
    inviteUrlData,
    inviteImageData,
    inviteUrlLoading,
    inviteImageLoading,
    inviteUrlLoaded,
    inviteImageLoaded,
    fetchInviteUrls,
    fetchInviteImages
  }
})
